import React from "react";
import BaseTable from "web_modules/shared/BaseTable";

import { Tradeline } from "@eave/shared/types/api";
import { formatCurrency } from "@eave/shared/utils/currency";
import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";
import BorrowerEntityUtil from "@eave/shared/utils/borrowers";

interface Props {
  liabilityReserves: Tradeline[];
  borrowerEntity: number;
}

const LiabilityReserves: React.FC<Props> = ({ liabilityReserves, borrowerEntity }: Props) => {
  const tableHeaders = makeHeaderData({
    creditor_name: "Liability Name",
    account_number: "Account Number",
    borrower_entity: "Borrower",
    current_balance_amount: "Total Reserves",
  });

  const baseTableTitle = (borrowerEntity, liabilityReserves) => {
    const total = liabilityReserves.reduce((acc, curr) => {
      return acc + parseFloat(curr.current_balance_amount);
    }, 0);

    return `Liabilities: ${formatCurrency(total, 2, 2)}`;
  };

  const sortedLiabilityReserves = liabilityReserves.sort((a, b) => {
    const entities_a = new BorrowerEntityUtil(a.borrower_entity);
    const entities_b = new BorrowerEntityUtil(b.borrower_entity);

    return entities_a.decomposed(entities_a).length - entities_b.decomposed(entities_b).length;
  });

  // LiabilityReserves are a subset of the tradeslines, so they're of type 'Tradeline'
  type modifiedTradelineProperties =
    | "account_number"
    | "borrower_entity"
    | "current_balance_amount";
  type TradelineTableRow = Omit<Tradeline, modifiedTradelineProperties> & {
    account_number: JSX.Element;
    borrower_entity: string;
    current_balance_amount: string;
  };

  /**
   * Given a borrower entity, returns the associated borrowers in a
   * csv format, e.g: 3 = "B1, B2"
   * @param {number} borrowerEntity The borrower entity
   * @returns
   */
  const getBorrowersFromLiability = (borrowerEntity: number) => {
    const entities = new BorrowerEntityUtil(borrowerEntity);
    const borrowers = entities.decomposed(entities).map(e => {
      switch (e) {
        case 1:
          return "B1";
        case 2:
          return "B2";
        case 4:
          return "B3";
        case 8:
          return "B4";
      }
      return e;
    });
    return borrowers.join(", ");
  };

  /**
   * Given a liability reserve, return a data object for the table.
   * @param {Tradeline} lr The liability reserve to display
   * @returns
   */
  const liabilityReserveToTableRow = (lr: Tradeline): TradelineTableRow => {
    const { account_number, borrower_entity, current_balance_amount } = lr;
    return {
      ...lr,
      account_number: (
        <span style={{ fontStyle: "italic", fontSize: "16px" }}>{account_number}</span>
      ),
      borrower_entity: getBorrowersFromLiability(borrower_entity),
      current_balance_amount: formatCurrency(current_balance_amount, 2, 2),
    };
  };

  return (
    <>
      {liabilityReserves && (
        <BaseTable
          disableBatch
          title={baseTableTitle(borrowerEntity, liabilityReserves)}
          headerData={tableHeaders}
          rawRowData={liabilityReserves}
          rowData={sortedLiabilityReserves.map(liabilityReserveToTableRow)}
          emptyRowText="No liability reserves."
          disableAddNew
          disableOverflow
          collapseOverride={liabilityReserves.length === 0}
        />
      )}
    </>
  );
};

export default LiabilityReserves;
